/**
 * /p/entrega/:token — Pagina publica de assinatura digital de recebimento.
 *
 * Cliente abre o link, ve os dados da entrega e confirma recebimento
 * com nome + opcional CPF + checkbox aceito_termos.
 *
 * Pattern reusado de /p/contrato/:token (ADR-0021).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { ACCESS_API_URL } from "../../config/api";


const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f4f5;
  color: #18181b;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Header = styled.header`
  padding: 24px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e4e7;
  text-align: center;
`;

const Main = styled.main`
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 16px;
  width: 100%;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const Button = styled.button`
  padding: 12px 24px;
  background: ${({ $primary }) => ($primary ? "#C2683A" : "#e4e4e7")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#18181b")};
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  font-size: 16px;
  margin-bottom: 12px;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
`;

const StatusBox = styled.div`
  padding: 16px;
  border-radius: 6px;
  background: ${({ $tone }) =>
    $tone === "success" ? "#d1fae5"
    : $tone === "warning" ? "#fef3c7"
    : $tone === "danger" ? "#fee2e2"
    : "#e0f2fe"};
  color: ${({ $tone }) =>
    $tone === "success" ? "#065f46"
    : $tone === "warning" ? "#92400e"
    : $tone === "danger" ? "#991b1b"
    : "#075985"};
  font-weight: 600;
  margin-bottom: 16px;
  text-align: center;
`;


const API_URL = ACCESS_API_URL;


export default function EntregaPublica() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);

  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [aceito, setAceito] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/publico/entrega/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detalhe || `Erro ${res.status}`);
      }
      const data = await res.json();
      setDados(data);
      if (data.assinante_nome) setNome(data.assinante_nome);
    } catch (err) {
      setError(err.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleAssinar = async () => {
    setEnviando(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/publico/entrega/${token}/assinar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome, cpf, aceito_termos: aceito }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detalhe || `Erro ${res.status}`);
      }
      setSucesso(true);
      await carregar();
    } catch (err) {
      setError(err.message || "Erro ao assinar.");
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <h1 style={{ margin: 0, fontSize: 24 }}>PharuERP</h1>
          <p style={{ margin: "8px 0 0", opacity: 0.7 }}>Carregando...</p>
        </Header>
      </Container>
    );
  }

  if (error && !dados) {
    return (
      <Container>
        <Header>
          <h1 style={{ margin: 0, fontSize: 24 }}>PharuERP</h1>
        </Header>
        <Main>
          <Card>
            <StatusBox $tone="danger">{error}</StatusBox>
            <p style={{ textAlign: "center", opacity: 0.7 }}>
              Se você está recebendo este link, peça um novo ao vendedor.
            </p>
          </Card>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1 style={{ margin: 0, fontSize: 24 }}>PharuERP</h1>
        <p style={{ margin: "8px 0 0", opacity: 0.7 }}>
          Confirmação de recebimento
        </p>
      </Header>

      <Main>
        {dados.ja_assinada || sucesso ? (
          <Card>
            <StatusBox $tone="success">
              Recebimento confirmado com sucesso!
            </StatusBox>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div><strong>Pedido:</strong> {dados.pedido_numero}</div>
              <div><strong>Cliente:</strong> {dados.cliente_nome}</div>
              <div><strong>Endereço:</strong> {dados.endereco_entrega}</div>
              <div><strong>Assinado por:</strong> {dados.assinante_nome}</div>
              <div><strong>Em:</strong> {new Date(dados.assinada_em).toLocaleString("pt-BR")}</div>
            </div>
            <p style={{ marginTop: 16, fontSize: 13, opacity: 0.7, textAlign: "center" }}>
              Você pode fechar esta página. Uma cópia foi registrada no PharuERP.
            </p>
          </Card>
        ) : (
          <>
            <Card>
              <h2 style={{ marginTop: 0 }}>Dados do recebimento</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div><strong>Pedido:</strong> {dados.pedido_numero}</div>
                <div><strong>Cliente:</strong> {dados.cliente_nome}</div>
                <div><strong>Endereço:</strong> {dados.endereco_entrega}</div>
                <div>
                  <strong>Entregue em:</strong>{" "}
                  {dados.entregue_em
                    ? new Date(dados.entregue_em).toLocaleString("pt-BR")
                    : "—"}
                </div>
              </div>
              {/* CRM-38: quem o cliente cadastrou como autorizado a receber.
                  O link não comprova identidade, então isto orienta sem
                  bloquear: outra pessoa pode assinar e a logística confere. */}
              {(dados.autorizados || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>Quem pode assinar o recebimento</strong>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    {dados.autorizados.map((autorizado) => (
                      <li key={`${autorizado.papel}-${autorizado.nome}`}>
                        {autorizado.nome}
                        {autorizado.papel ? ` — ${autorizado.papel}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.75 }}>
                    Se quem recebeu não está na lista, informe o nome de quem assina mesmo
                    assim: a entrega é registrada e a marcenaria confere depois.
                  </p>
                </div>
              )}
            </Card>

            <Card>
              <h2 style={{ marginTop: 0 }}>Confirmar recebimento</h2>
              <p style={{ marginBottom: 16, opacity: 0.8 }}>
                Ao confirmar, você declara que recebeu o pedido nas condições
                acordadas. Esta confirmação tem validade jurídica
                (Lei 14.063/2020).
              </p>
              {error && <StatusBox $tone="danger">{error}</StatusBox>}

              <Label>
                Seu nome completo *
                <Input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  maxLength={120}
                  placeholder="Digite seu nome"
                  required
                />
              </Label>

              <Label>
                CPF (opcional)
                <Input
                  type="text"
                  value={cpf}
                  onChange={(e) => setCpf(e.target.value)}
                  maxLength={14}
                  placeholder="000.000.000-00"
                />
              </Label>

              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, marginBottom: 16 }}>
                <input
                  type="checkbox"
                  checked={aceito}
                  onChange={(e) => setAceito(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  Eu confirmo o recebimento do pedido conforme acima e
                  declaro que os produtos foram entregues nas condicoes
                  acordadas. *
                </span>
              </label>

              <Button
                $primary
                onClick={handleAssinar}
                disabled={!nome.trim() || !aceito || enviando}
              >
                {enviando ? "Confirmando..." : "Confirmar recebimento"}
              </Button>
            </Card>
          </>
        )}
      </Main>
    </Container>
  );
}
